import React, { useEffect, useState } from 'react';
import { Alert, Col, Pagination, Row, Select, Spin } from 'antd';
import { officialSchoolAPI } from '../../services/api';

const emptySelection = { province: null, city: null, county: null, category: null, school: null };

const optionsFor = (items = []) => items.map((item) => ({ value: item.code, label: item.name }));

const OfficialSchoolPicker = ({ value, onChange, disabled = false }) => {
  const [selection, setSelection] = useState(emptySelection);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [counties, setCounties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schools, setSchools] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const updateSelection = (next) => {
    setSelection(next);
    onChange?.(next.school);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await officialSchoolAPI.getProvinces();
        if (active && response.data.success) setProvinces(response.data.data || []);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (value) return;
    setSelection(emptySelection);
    setCities([]);
    setCounties([]);
    setCategories([]);
    setSchools([]);
    setPage(1);
    setTotal(0);
  }, [value]);

  const loadSchools = async (countyCode, categoryCode, requestedPage = 1) => {
    setLoading(true);
    try {
      const response = await officialSchoolAPI.getSchools({ countyCode, category: categoryCode.code, categoryName: categoryCode.name, page: requestedPage, limit: 50 });
      if (response.data.success) {
        const data = response.data.data || {};
        setSchools(data.items || []);
        setPage(data.page || requestedPage);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProvince = async (provinceCode) => {
    const province = provinces.find((item) => item.code === provinceCode) || null;
    updateSelection({ ...emptySelection, province });
    setCities([]); setCounties([]); setCategories([]); setSchools([]); setTotal(0); setPage(1);
    if (!province) return;
    setLoading(true);
    try {
      const response = await officialSchoolAPI.getCities(province.code);
      if (response.data.success) setCities(response.data.data || []);
    } finally { setLoading(false); }
  };

  const handleCity = async (cityCode) => {
    const city = cities.find((item) => item.code === cityCode) || null;
    updateSelection({ ...emptySelection, province: selection.province, city });
    setCounties([]); setCategories([]); setSchools([]); setTotal(0); setPage(1);
    if (!city) return;
    setLoading(true);
    try {
      const response = await officialSchoolAPI.getCounties(city.code);
      if (response.data.success) setCounties(response.data.data || []);
    } finally { setLoading(false); }
  };

  const handleCounty = async (countyCode) => {
    const county = counties.find((item) => item.code === countyCode) || null;
    updateSelection({ ...emptySelection, province: selection.province, city: selection.city, county });
    setCategories([]); setSchools([]); setTotal(0); setPage(1);
    if (!county) return;
    setLoading(true);
    try {
      const response = await officialSchoolAPI.getCategories(county.code);
      if (response.data.success) setCategories(response.data.data || []);
    } finally { setLoading(false); }
  };

  const handleCategory = async (selectionKey) => {
    const category = categories.find((item) => item.selectionKey === selectionKey) || null;
    const next = { ...emptySelection, province: selection.province, city: selection.city, county: selection.county, category };
    updateSelection(next);
    setSchools([]); setTotal(0); setPage(1);
    if (category && selection.county) await loadSchools(selection.county.code, category, 1);
  };

  const handleSchool = (officialSchoolCode) => {
    const school = schools.find((item) => item.officialSchoolCode === officialSchoolCode) || null;
    updateSelection({ ...selection, school });
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12} xl={6}><Select value={selection.province?.code} options={optionsFor(provinces)} onChange={handleProvince} disabled={disabled} placeholder="选择省份" style={{ width: '100%' }} /></Col>
        <Col xs={24} md={12} xl={6}><Select value={selection.city?.code} options={optionsFor(cities)} onChange={handleCity} disabled={disabled || !selection.province} placeholder="选择城市" style={{ width: '100%' }} /></Col>
        <Col xs={24} md={12} xl={6}><Select value={selection.county?.code} options={optionsFor(counties)} onChange={handleCounty} disabled={disabled || !selection.city} placeholder="选择区县" style={{ width: '100%' }} /></Col>
        <Col xs={24} md={12} xl={6}><Select value={selection.category?.selectionKey} options={categories.map((item) => ({ value: item.selectionKey, label: `${item.name} (${item.count})` }))} onChange={handleCategory} disabled={disabled || !selection.county} placeholder="选择学校类型" style={{ width: '100%' }} /></Col>
        <Col span={24}>
          <Select value={value?.officialSchoolCode} options={schools.map((school) => ({ value: school.officialSchoolCode, label: school.name }))} onChange={handleSchool} disabled={disabled || !selection.category} placeholder="请选择学校" style={{ width: '100%' }} showSearch optionFilterProp="label" />
        </Col>
      </Row>
      {selection.category && total > 50 && <Pagination current={page} total={total} pageSize={50} size="small" showSizeChanger={false} showTotal={(count) => `本区县该类型共 ${count} 所`} style={{ marginTop: 12 }} onChange={(nextPage) => loadSchools(selection.county.code, selection.category, nextPage)} />}
      {selection.category && total === 0 && !loading && <Alert type="info" showIcon message="该区县暂无此类型学校" style={{ marginTop: 12 }} />}
    </Spin>
  );
};

export default OfficialSchoolPicker;
